import { nodesFlux } from '@candulabs/craft';
import flatten from 'lodash/flatten';
import { useMemo } from 'react';

import { ContentType } from '@candu-ai/content/types';
import { BuiltInComponents, CRAFT_ROOT_ID } from 'craft/constants';
import {
  useEditor,
  useEditorSelector,
  useFindAncestorWithType,
  useMultiNodes,
  useSelection,
} from 'craft/hooks';
import { useActiveTooltipFromRedux } from 'craft/state';
import { Action, CATEGORIES, CraftNode, HANDLER_TYPES } from 'craft/types';

export const useTooltipChildren = (): string[] => {
  const childSelector = useMemo(() => nodesFlux.selectors.getNodeChildren(CRAFT_ROOT_ID), []);
  return useEditorSelector(childSelector);
};

export const useActiveTooltip = (): CraftNode | undefined => {
  const { selection } = useSelection();
  const { query } = useEditor();
  const activeHotspotFromRedux = useActiveTooltipFromRedux();
  const tooltipNodeIds = useTooltipChildren();
  const tooltipType = useMemo(() => query.node(tooltipNodeIds[0])?.get()?.data?.name, [
    tooltipNodeIds,
  ]) as BuiltInComponents;
  const tooltipNodeId =
    useFindAncestorWithType(selection.first, tooltipType) || activeHotspotFromRedux;

  const firstNode = query.node(tooltipNodeIds.find((id) => id === tooltipNodeId))?.get();

  // sanity check...
  if (firstNode?.data.name !== tooltipType) {
    return undefined;
  }

  return firstNode as CraftNode;
};

interface TooltipConnection {
  fromTooltipId: string;
  toTooltipId: string;
  actionNodeId: string;
  handlerType: HANDLER_TYPES;
}

// TODO: this is a variation of useFlow to find disconnected tour steps,
//  we should attempt to DRY it in the future
export const useTooltipFlow = (contentType: ContentType) => {
  const { query } = useEditor();
  const { selection } = useSelection();
  const tooltipNodeIds = useTooltipChildren();

  const { nodeActions } = useMultiNodes(selection.all, (node) => ({
    nodeActions: node?.data?.custom?.actions || [],
  }));

  const getStepIndex = (tooltipId: string): number => {
    return tooltipNodeIds.findIndex((id) => id === tooltipId);
  };

  const getNextTooltipId = (tooltipId: string): string | undefined => {
    const stepIdIndex = getStepIndex(tooltipId);

    if (stepIdIndex + 1 > tooltipNodeIds.length) {
      return undefined;
    }

    return tooltipNodeIds[stepIdIndex + 1];
  };

  const getPreviousTooltipId = (tooltipId: string): string | undefined => {
    const stepIdIndex = getStepIndex(tooltipId);

    if (stepIdIndex === 0) {
      return undefined;
    }

    return tooltipNodeIds[stepIdIndex - 1];
  };

  const tooltipNodes = tooltipNodeIds.map((tooltipId) => ({
    nodeId: tooltipId,
    distance: -1,
  }));

  const getConnectionsForNode = (node: CraftNode, tooltipId: string) => {
    const { actions } = node.data.custom || {};

    if (!actions || !actions.length || !actions[0]) {
      return [];
    }

    return actions.reduce((acc: TooltipConnection[], action: Action) => {
      if (action.category !== CATEGORIES.TOUR) {
        return acc;
      }
      const type = action.handler?.type;
      if (!type) {
        return acc;
      }

      const actionNodeId = node.id;

      if (type === HANDLER_TYPES.NEXT_TOUR_STEP) {
        const nextTooltipId = getNextTooltipId(tooltipId);

        if (!nextTooltipId) {
          return acc;
        }

        return [
          ...acc,
          {
            actionNodeId,
            handlerType: type,
            fromTooltipId: tooltipId,
            toTooltipId: nextTooltipId,
          },
        ];
      }
      if (type === HANDLER_TYPES.PREVIOUS_TOUR_STEP) {
        const previousTooltipId = getPreviousTooltipId(tooltipId);

        if (!previousTooltipId) {
          return acc;
        }

        return [
          ...acc,
          {
            actionNodeId,
            handlerType: type,
            fromTooltipId: tooltipId,
            toTooltipId: previousTooltipId,
          },
        ];
      }

      return acc;
    }, []);
  };

  const findConnections = (nodeId: string, tooltipId: string): TooltipConnection[] => {
    const node = query.node(nodeId).get();
    if (!node) {
      return [];
    }
    const edges = getConnectionsForNode(node as CraftNode, tooltipId);

    return flatten([edges, ...(node.data.nodes || []).map((n) => findConnections(n, tooltipId))]);
  };

  const connections = flatten(
    tooltipNodes.map(({ nodeId: tooltipId }) => findConnections(tooltipId, tooltipId)),
  );

  const findDistance = (
    currentStepId: string,
    currentDistance: number,
    visitedSteps: Set<string>,
  ): Record<string, number> => {
    // I've already seen this node. Don't come back...
    if (visitedSteps.has(currentStepId)) {
      return {};
    }
    const updatedVisitedSteps = visitedSteps.add(currentStepId);
    // let's check out all the beautiful nodes that this node is going to
    const moreDistances = connections
      .filter((edge) => edge.fromTooltipId === currentStepId)
      .reduce(
        (obj, edge) => ({
          ...obj,
          ...findDistance(edge.toTooltipId, currentDistance + 1, updatedVisitedSteps),
        }),
        {},
      );

    return { ...moreDistances, [currentStepId]: currentDistance };
  };

  const distances = useMemo(() => {
    if (!tooltipNodes[0] || contentType !== ContentType.Tour) {
      return null;
    }

    return findDistance(tooltipNodes[0].nodeId, 0, new Set());
  }, [tooltipNodes, contentType, nodeActions]);

  const isTooltipConnected = (tooltipId: string) => !distances || distances[tooltipId] > -1;

  return {
    isTooltipConnected,
    getNextTooltipId,
    getPreviousTooltipId,
  };
};
